import { useCallback, useEffect, useMemo, useState } from 'react'
import { ReadyState } from 'react-use-websocket'

import { useWebSocketMessage } from '@features/websocket'

import { useFeatureFlags } from '@context/FeatureFlagsContext'

import { useWebSocketContext } from '../WebSocketContext'
import type { WebSocketMessage } from '../core/types'

export const TICKS_MARKET_DATA_EVENT_TYPE = 'ticks_market_data_event'

export type MarketDataTick = {
  Timestamp: string
  Bid: number
  Ask: number
}

export type TicksMarketDataEvent = WebSocketMessage & {
  Id: string
  ClientId: string
  type: typeof TICKS_MARKET_DATA_EVENT_TYPE
  TimestampMsUtc: number
  Data: {
    Symbol: string
    TimestampMsUtc: number
    Ticks: MarketDataTick[]
  }
}

const isTicksMarketDataEvent = (message: WebSocketMessage): message is TicksMarketDataEvent =>
  message.type === TICKS_MARKET_DATA_EVENT_TYPE

export type TicksMarketDataBySymbol = Record<string, TicksMarketDataEvent | undefined>
export type LatestTicksMarketDataBySymbol = Record<string, MarketDataTick | undefined>

export type TicksMarketDataOptions = {
  enabled?: boolean
  onEvent?: (event: TicksMarketDataEvent) => void
}

const useMarketDataSymbols = (symbols: readonly string[]): string[] => {
  const key = [...new Set(symbols.map((symbol) => symbol.trim()).filter(Boolean))].sort().join('|')

  return useMemo(() => (key ? key.split('|') : []), [key])
}

const useTicksMarketDataEventListener = (
  symbols: readonly string[] | undefined,
  onEvent: ((event: TicksMarketDataEvent) => void) | undefined,
  enabled: boolean
): void => {
  const symbolSet = useMemo(() => (symbols ? new Set(symbols) : undefined), [symbols])

  useWebSocketMessage(
    TICKS_MARKET_DATA_EVENT_TYPE,
    (message) => {
      if (!isTicksMarketDataEvent(message)) return
      if (symbolSet && !symbolSet.has(message.Data.Symbol)) return

      onEvent?.(message)
    },
    { enabled }
  )
}

export const useTicksMarketDataEvents = (
  onEvent: (event: TicksMarketDataEvent) => void,
  { enabled = true }: { enabled?: boolean } = {}
): void => {
  const { marketDataWSType } = useFeatureFlags()

  useTicksMarketDataEventListener(
    undefined,
    onEvent,
    enabled && marketDataWSType === 'notifications-hub'
  )
}

export const useTicksMarketDataSubscription = (
  symbols: readonly string[],
  { enabled = true, onEvent }: TicksMarketDataOptions = {}
) => {
  const { marketDataWSType } = useFeatureFlags()
  const { subscribeTicksMarketData, readyState, isConnected } = useWebSocketContext()
  const isEnabled = enabled && marketDataWSType === 'notifications-hub'
  const activeSymbols = useMarketDataSymbols(isEnabled ? symbols : [])

  useTicksMarketDataEventListener(activeSymbols, onEvent, isEnabled)

  useEffect(() => {
    if (!activeSymbols.length) return
    return subscribeTicksMarketData(activeSymbols)
  }, [activeSymbols, subscribeTicksMarketData])

  return {
    symbols: activeSymbols,
    readyState: isEnabled ? readyState : ReadyState.UNINSTANTIATED,
    isConnected: isEnabled && isConnected,
  }
}

export const useTicksMarketData = (
  symbols: readonly string[],
  { enabled = true, onEvent }: TicksMarketDataOptions = {}
) => {
  const [events, setEvents] = useState<TicksMarketDataBySymbol>({})
  const handleEvent = useCallback(
    (event: TicksMarketDataEvent) => {
      setEvents((current) => ({ ...current, [event.Data.Symbol]: event }))
      onEvent?.(event)
    },
    [onEvent]
  )
  const subscription = useTicksMarketDataSubscription(symbols, {
    enabled,
    onEvent: handleEvent,
  })
  const { symbols: activeSymbols, isConnected } = subscription

  useEffect(() => {
    if (isConnected) return
    setEvents((current) => (Object.keys(current).length ? {} : current))
  }, [isConnected])

  const data = useMemo(
    () =>
      Object.fromEntries(
        activeSymbols.map((symbol) => [symbol, events[symbol]])
      ) as TicksMarketDataBySymbol,
    [activeSymbols, events]
  )
  const latestTicks = useMemo(
    () =>
      Object.fromEntries(
        activeSymbols.map((symbol) => {
          const ticks = data[symbol]?.Data.Ticks
          return [symbol, ticks?.[ticks.length - 1]]
        })
      ) as LatestTicksMarketDataBySymbol,
    [activeSymbols, data]
  )

  return { ...subscription, data, latestTicks }
}
